import { z } from 'zod';
import { RuntimeType } from './gameManifest.js';

/**
 * GameIdea - A structured game idea generated by the Game Hunter Agent.
 *
 * This is NOT prose. Every field is machine-readable so ideas can be
 * scored, compared, sorted, and fed directly into the Creator pipeline.
 */
export const GameIdeaSchema = z.object({
  /** Human-readable game title. */
  title: z.string().min(1).max(100),
  /** Which template to use: clicker | reaction | memory | puzzle | custom. */
  template: z.string().min(1).max(50),
  /** Runtime: phaser for action/reaction, dom for clicker/UI/card. */
  runtime: RuntimeType,
  /** Visual theme: neon | space | ocean | forest | retro | minimal | ... */
  theme: z.string().min(1).max(50),
  /** Expected play duration in seconds. */
  duration: z.number().int().min(5).max(600),
  /** One-sentence description of the core mechanic. */
  core_mechanic: z.string().min(1).max(300),
  /** Target audience: casual | midcore | kids | adults | seniors. */
  target_audience: z.string().min(1).max(50),
  /** Difficulty level. */
  difficulty: z.enum(['easy', 'medium', 'hard', 'expert']),
  /** Why this idea was chosen (data-driven rationale). */
  reason: z.string().min(1).max(500),
  /** Expected retention rate 0-1. */
  expected_retention: z.number().min(0).max(1),
});

export type GameIdea = z.infer<typeof GameIdeaSchema>;

/**
 * ScoredGameIdea - A GameIdea after the Hunter's scoring pass.
 *
 * The score is a composite 0-100 with a transparent breakdown so the
 * selection logic is auditable, not a black box.
 */
export const ScoreBreakdownSchema = z.object({
  /** How well the idea fits observed player preferences (0-100). */
  market_fit: z.number().min(0).max(100),
  /** How novel / differentiated the idea is (0-100). */
  novelty: z.number().min(0).max(100),
  /** How feasible it is to build with current templates (0-100). */
  feasibility: z.number().min(0).max(100),
  /** Projected retention score (0-100, derived from expected_retention). */
  expected_retention: z.number().min(0).max(100),
});

export type ScoreBreakdown = z.infer<typeof ScoreBreakdownSchema>;

export const ScoredGameIdeaSchema = GameIdeaSchema.extend({
  /** Composite score 0-100. */
  score: z.number().min(0).max(100),
  /** Transparent breakdown of how the score was computed. */
  score_breakdown: ScoreBreakdownSchema,
  /** Rank after sorting (1 = best). */
  rank: z.number().int().min(1),
});

export type ScoredGameIdea = z.infer<typeof ScoredGameIdeaSchema>;

/**
 * HunterContext - Input bundle for the Game Hunter Agent's daily run.
 */
export interface HunterContext {
  /** Historical game performance summaries (from analytics). */
  gameAnalytics: GameAnalyticsSummary[];
  /** Available template IDs the Hunter may choose from. */
  availableTemplates: string[];
  /** Available runtimes the Hunter may choose from. */
  availableRuntimes: RuntimeType[];
  /** How many candidate ideas to generate (default 20). */
  candidateCount: number;
  /** Optional LLM gateway for LLM-backed idea generation. */
  llmGateway?: unknown;
}

/**
 * GameAnalyticsSummary - Per-game performance rollup consumed by the Hunter.
 */
export const GameAnalyticsSummarySchema = z.object({
  game_id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  template: z.string().optional(),
  runtime: RuntimeType.optional(),
  theme: z.string().optional(),
  plays: z.number().int().min(0),
  unique_players: z.number().int().min(0),
  average_session_duration: z.number().min(0),
  average_score: z.number().min(0),
  completion_rate: z.number().min(0).max(100),
  retry_rate: z.number().min(0).max(100),
  calculated_at: z.string().datetime(),
});

export type GameAnalyticsSummary = z.infer<typeof GameAnalyticsSummarySchema>;
